import { Injectable } from '@angular/core';

/**
 * Service to track notification correlation UUIDs to prevent duplicate toasts
 * when both API responses and websocket messages arrive for the same notification.
 *
 * When the frontend initiates a notification:
 * 1. Generate a correlation UUID
 * 2. Track it before making the API request
 * 3. When websocket message arrives with the same UUID, suppress the toast
 */
@Injectable({
  providedIn: 'root',
})
export class NotificationTrackingService {
  // Track correlation UUIDs of notifications initiated from this frontend instance
  // These are tracked for 30 seconds to avoid showing duplicate websocket toasts
  private readonly trackedCorrelationUuids = new Set<string>();

  /**
   * Track a correlation UUID for a notification request
   * Call this BEFORE making the API request
   * The UUID will be automatically cleaned up after 60 seconds
   */
  trackNotificationRequest(correlationUuid: string): void {
    this.trackedCorrelationUuids.add(correlationUuid);

    // Clean up after 60 seconds to avoid memory leaks
    setTimeout(() => {
      this.trackedCorrelationUuids.delete(correlationUuid);
    }, 60000);
  }

  /**
   * Check if a notification should be suppressed (not show a toast)
   * Returns true if the correlation UUID matches one we sent from this frontend instance
   */
  shouldSuppressNotification(correlationUuid: string | null | undefined): boolean {
    if (!correlationUuid) {
      // If no correlation UUID, this is from an external source (workflow, etc.)
      // Don't suppress it
      return false;
    }
    
    return this.trackedCorrelationUuids.has(correlationUuid);
  }
}

